import { useCallback, useEffect, useMemo, useState } from "react";

type Mode = "light" | "dark";
type ThemePreference = "light" | "dark" | "system";

function reloadSvgImagesForColorScheme() {
  for (const img of document.images) {
    if (img.src.endsWith(".svg")) img.src = img.src;
  }
}

export function useColorMode({
  bindListeners,
  preference,
  onToggle,
}: {
  bindListeners?: boolean;
  preference?: ThemePreference | null;
  onToggle?: (mode: Mode) => void;
} = {}) {
  const root = useMemo(() => document.documentElement, []);
  const media = useMemo(
    () => window.matchMedia("(prefers-color-scheme: dark)"),
    []
  );

  const systemMode = useCallback(
    (): Mode => (media.matches ? "dark" : "light"),
    [media]
  );

  const resolvePreference = useCallback(
    (pref: ThemePreference | null | undefined): Mode => {
      if (pref === "light" || pref === "dark") return pref;
      return systemMode();
    },
    [systemMode]
  );

  const getDisplayedColorMode: () => Mode = useCallback(() => {
    return root.style.colorScheme !== ""
      ? (root.style.colorScheme as Mode)
      : systemMode();
  }, [root, systemMode]);

  // if we set this from `getDisplayedColorMode()` immediately, Chrome can
  // respond unreliably, so we wait until the effect
  const [displayedMode, setDisplayedMode] = useState<Mode | null>(null);

  const displayColorMode = useCallback(
    (mode: Mode) => {
      const changed = getDisplayedColorMode() !== mode;

      // work around bun prod light-dark bugs
      root.style.removeProperty(`--buncss-${mode}`);
      root.style.cssText = `--buncss-${mode === "light" ? "dark" : "light"}:;`;

      root.style.colorScheme = mode;
      if (changed) reloadSvgImagesForColorScheme();

      setDisplayedMode(mode);
    },
    [root, getDisplayedColorMode]
  );

  const toggleColorMode = useCallback(() => {
    const next = getDisplayedColorMode() === "dark" ? "light" : "dark";
    displayColorMode(next);
    onToggle?.(next);
  }, [getDisplayedColorMode, displayColorMode, onToggle]);

  // Match on code, not key: on macOS Option composes a glyph (⇧⌥D → Î).
  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!e.shiftKey || !e.altKey || e.metaKey || e.ctrlKey) return;
      if (e.code !== "KeyD") return;
      e.preventDefault();
      toggleColorMode();
    },
    [toggleColorMode]
  );

  const onMediaChange = useCallback(
    (event: MediaQueryListEvent) => {
      if (preference && preference !== "system") return;
      displayColorMode(event.matches ? "dark" : "light");
    },
    [preference, displayColorMode]
  );

  useEffect(() => {
    if (!bindListeners) return;

    // handle system changes
    media.addEventListener("change", onMediaChange);
    return () => media.removeEventListener("change", onMediaChange);
  }, [bindListeners, media, onMediaChange]);

  useEffect(() => {
    if (!bindListeners) return;

    // Capture phase so the shortcut still fires while a text editor or the
    // composer (which stop keydown propagation) holds focus.
    root.addEventListener("keydown", onKeyDown, { capture: true });
    return () =>
      root.removeEventListener("keydown", onKeyDown, { capture: true });
  }, [bindListeners, root, onKeyDown]);

  useEffect(() => {
    // oxlint-disable-next-line react/react-compiler -- EffectSetState: syncs the resolved color mode to the document root colorScheme
    displayColorMode(resolvePreference(preference));
  }, [displayColorMode, resolvePreference, preference]);

  return [displayedMode, toggleColorMode] as [Mode, () => void];
}
